// Copyright 2026 Epic Digital Interactive Media LLC
// SPDX-License-Identifier: Apache-2.0

import type { RxDatabase } from 'rxdb';
import { QueryOperator } from '../operators/QueryOperator';
import { MutationOperator } from '../operators/MutationOperator';
import type { QueryOptions } from '../operators/types';
import type { ApprovalModel } from '../../models/Approval';
import { createId } from '../../index';

export interface ApprovalFilters {
  orgId: string;
  workspaceId?: string;
  projectId?: string;
  milestoneId?: string;
  status?: ApprovalModel['status'];
  type?: ApprovalModel['type'];
  category?: ApprovalModel['category'];
  userId?: string;
}

export interface CreateApprovalInput {
  projectId: string;
  milestoneId?: string;
  taskId?: string;
  documentId?: string;
  title: string;
  type: ApprovalModel['type'];
  category: ApprovalModel['category'];
  categoryName: string;
  documentType?: string;
  documentContent?: string;
  orgId: string;
  workspaceId: string;
  userId: string;
}

export interface UpdateApprovalInput {
  id: string;
  updates: Partial<ApprovalModel>;
}

export class ApprovalOperator {
  constructor(private db: RxDatabase) {}

  query(
    filters: ApprovalFilters,
    options?: { sort?: string; limit?: number; skip?: number }
  ): QueryOperator<ApprovalModel> {
    const collection = this.db.collections.approvals;

    const selector: any = {
      orgId: { $eq: filters.orgId },
    };

    if (filters.workspaceId) {
      selector.workspaceId = { $eq: filters.workspaceId };
    }

    if (filters.projectId) {
      selector.projectId = { $eq: filters.projectId };
    }

    if (filters.milestoneId) {
      selector.milestoneId = { $eq: filters.milestoneId };
    }

    if (filters.status) {
      selector.status = { $eq: filters.status };
    }

    if (filters.type) {
      selector.type = { $eq: filters.type };
    }

    if (filters.category) {
      selector.category = { $eq: filters.category };
    }

    if (filters.userId) {
      selector.userId = { $eq: filters.userId };
    }

    const queryOptions: QueryOptions<ApprovalModel> = {
      selector,
      sort: options?.sort ? [{ [options.sort]: 'desc' }] : [{ createdAt: 'desc' }],
      limit: options?.limit,
      skip: options?.skip,
    };

    return new QueryOperator<ApprovalModel>(collection, queryOptions);
  }

  async findOne(approvalId: string): Promise<ApprovalModel | null> {
    const doc = await this.db.collections.approvals.findOne(approvalId).exec();
    return doc ? (doc.toJSON() as ApprovalModel) : null;
  }

  async count(filters: ApprovalFilters): Promise<number> {
    const collection = this.db.collections.approvals;
    const selector: any = {
      orgId: { $eq: filters.orgId },
    };

    if (filters.workspaceId) {
      selector.workspaceId = { $eq: filters.workspaceId };
    }

    if (filters.projectId) {
      selector.projectId = { $eq: filters.projectId };
    }

    if (filters.milestoneId) {
      selector.milestoneId = { $eq: filters.milestoneId };
    }

    if (filters.status) {
      selector.status = { $eq: filters.status };
    }

    if (filters.type) {
      selector.type = { $eq: filters.type };
    }

    if (filters.category) {
      selector.category = { $eq: filters.category };
    }

    if (filters.userId) {
      selector.userId = { $eq: filters.userId };
    }

    return await collection.count({ selector }).exec();
  }

  createMutation(): MutationOperator<ApprovalModel, CreateApprovalInput> {
    return new MutationOperator<ApprovalModel, CreateApprovalInput>(this.db.collections.approvals, {
      mutationFn: async (collection, input) => {
        const now = new Date().toISOString();
        const approval: ApprovalModel = {
          id: createId('approval'),
          projectId: input.projectId,
          milestoneId: input.milestoneId ?? '',
          taskId: input.taskId ?? '',
          documentId: input.documentId ?? '',
          title: input.title,
          type: input.type,
          category: input.category,
          categoryName: input.categoryName,
          status: 'pending',
          documentType: input.documentType,
          documentContent: input.documentContent,
          orgId: input.orgId,
          workspaceId: input.workspaceId,
          userId: input.userId,
          createdAt: now,
          updatedAt: now,
        };

        await collection.insert(approval);
        return approval;
      },
    });
  }

  updateMutation(): MutationOperator<ApprovalModel, UpdateApprovalInput> {
    return new MutationOperator<ApprovalModel, UpdateApprovalInput>(this.db.collections.approvals, {
      mutationFn: async (collection, { id, updates }) => {
        const doc = await collection.findOne(id).exec();
        if (!doc) throw new Error(`Approval ${id} not found`);

        // If status is being updated from pending, set respondedAt
        const currentData = doc.toJSON() as ApprovalModel;
        const updatesWithTimestamp: any = { ...updates, updatedAt: new Date().toISOString() };

        if (updates.status && currentData.status === 'pending' && updates.status !== 'pending') {
          updatesWithTimestamp.respondedAt = new Date().toISOString();
        }

        await doc.update({
          $set: updatesWithTimestamp,
        });

        return doc.toJSON() as ApprovalModel;
      },
    });
  }

  deleteMutation(): MutationOperator<void, string> {
    return new MutationOperator<void, string>(this.db.collections.approvals, {
      mutationFn: async (collection, approvalId) => {
        const doc = await collection.findOne(approvalId).exec();
        if (!doc) throw new Error(`Approval ${approvalId} not found`);

        // Approvals don't have an archived flag in the model, so we actually delete them
        await doc.remove();
      },
    });
  }
}
